import React, { useMemo } from "react";
import { graphql, useFragment } from "react-relay";
import { AHText, Box, Space } from "arkh";

import { SharedValue } from "react-native-reanimated";
import { TeamScheduleAdminItemComponent } from "./TeamScheduleAdminItemComponent";
import { DateTime } from "luxon";
import { TeamScheduleWeeklyWeekdayIndicator } from "../TeamScheduleWeeklyWeekdayIndicator";
import { TeamScheduleAdminItemTimeIndicator_RoutineScheduleDaily$key } from "./__generated__/TeamScheduleAdminItemTimeIndicator_RoutineScheduleDaily.graphql";
import { TeamScheduleAdminItemWeekdayIndicator_RoutineScheduleDaily$key } from "./__generated__/TeamScheduleAdminItemWeekdayIndicator_RoutineScheduleDaily.graphql";
import { TeamScheduleAdminItemMonthDayIndicator_RoutineScheduleMonthly$key } from "./__generated__/TeamScheduleAdminItemMonthDayIndicator_RoutineScheduleMonthly.graphql";
import { TeamScheduleAdminItem_routine$key } from "./__generated__/TeamScheduleAdminItem_routine.graphql";
import { TeamScheduleListItemMonthlyRangeComponent } from "../../Team/TeamHomeNavigator/TeamScheduleListItem/TeamScheduleListItemScheduleRange/TeamScheduleListItemMonthlyRange";
import { useWeekdayFormattedString } from "../../../useWeekdayFormattedString";

export function TeamScheduleAdminItem(props: {
  spaceTop?: Space;
  isFirst: boolean;
  isLast: boolean;
  routine: TeamScheduleAdminItem_routine$key;
  itemType: "timeOfDay" | "WEEKLY" | "MONTHLY";
  newlyCreatedId: SharedValue<string | null>;
}) {
  const routine = useFragment(
    graphql`
      fragment TeamScheduleAdminItem_routine on Routine {
        ...TeamScheduleAdminItemComponent_routine
        currentSchedule {
          ... on RoutineScheduleMonthly {
            __typename
            endDay
            startDay
            ...TeamScheduleAdminItemMonthDayIndicator_RoutineScheduleMonthly
          }
          ... on RoutineScheduleWeekly {
            __typename
            ...TeamScheduleWeeklyWeekdayIndicator_RoutineScheduleWeekly
          }
          ... on RoutineScheduleDaily {
            __typename
            isAllDay
            weekdays {
              startTime {
                hour
                minute
              }
            }
            deadlineDuration
            ...TeamScheduleAdminItemWeekdayIndicator_RoutineScheduleDaily
            ...TeamScheduleAdminItemTimeIndicator_RoutineScheduleDaily
          }
        }
      }
    `,
    props.routine
  );
  if (
    !(
      routine.currentSchedule?.__typename === "RoutineScheduleDaily" ||
      routine.currentSchedule?.__typename === "RoutineScheduleWeekly" ||
      routine.currentSchedule?.__typename === "RoutineScheduleMonthly"
    )
  ) {
    return null;
  }

  const { routine: r, ...rest } = props;

  return (
    <TeamScheduleAdminItemComponent
      {...rest}
      spaceTop={props.spaceTop}
      routine={routine}
      timeIndicator={
        routine.currentSchedule.__typename === "RoutineScheduleDaily" &&
        !routine.currentSchedule.isAllDay ? (
          <TimeIndicator schedule={routine.currentSchedule} />
        ) : (
          <Box spaceLeft="medium" />
        )
      }
      activeOccasionsIndicator={
        routine.currentSchedule.__typename === "RoutineScheduleDaily" ? (
          <WeekdayIndicator schedule={routine.currentSchedule} />
        ) : routine.currentSchedule.__typename === "RoutineScheduleWeekly" ? (
          <TeamScheduleWeeklyWeekdayIndicator
            schedule={routine.currentSchedule}
          />
        ) : routine.currentSchedule.__typename === "RoutineScheduleMonthly" ? (
          <MonthDayIndicator schedule={routine.currentSchedule} />
        ) : (
          <Box />
        )
      }
    />
  );
}

export const TeamScheduleAdminItemTimeContainerWidth = 60;

function TeamScheduleAdminItemTimeContainer(
  props: React.PropsWithChildren<object>
) {
  return (
    <Box
      style={{
        width: TeamScheduleAdminItemTimeContainerWidth,
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      {props.children}
    </Box>
  );
}

function TimeIndicator(props: {
  schedule: TeamScheduleAdminItemTimeIndicator_RoutineScheduleDaily$key;
}) {
  const schedule = useFragment(
    graphql`
      fragment TeamScheduleAdminItemTimeIndicator_RoutineScheduleDaily on RoutineScheduleDaily {
        weekdays {
          startTime {
            hour
            minute
          }
        }
        deadlineDuration
      }
    `,
    props.schedule
  );

  const startAtMin = useMemo(() => {
    const startTime =
      schedule.weekdays.length > 0 ? schedule.weekdays[0].startTime : null;
    if (startTime !== null) {
      return startTime.hour * 60 + startTime.minute;
    }
    return 0;
  }, [schedule.weekdays]);

  const startAt = useMemo(() => {
    return DateTime.fromObject({
      minute: 0,
      hour: 0,
      second: 0,
    }).plus({ minutes: startAtMin });
  }, [startAtMin]);

  const deadlineAtString = useMemo(() => {
    if (schedule.deadlineDuration === null) {
      return null;
    }

    return startAt
      .plus({ seconds: schedule.deadlineDuration })
      .toLocaleString(DateTime.TIME_24_SIMPLE);
  }, [schedule.deadlineDuration, startAt]);

  return (
    <TeamScheduleAdminItemTimeContainer>
      <AHText
        type="info-small"
        style={{ textAlign: "center", textAlignVertical: "center" }}
      >
        {startAt.toLocaleString(DateTime.TIME_24_SIMPLE)}
      </AHText>
      {deadlineAtString ? (
        <AHText
          spaceTop="xxsmall"
          type="info-small"
          color="washedText"
          style={{ textAlign: "center", textAlignVertical: "center" }}
        >
          {deadlineAtString}
        </AHText>
      ) : null}
    </TeamScheduleAdminItemTimeContainer>
  );
}

function MonthDayIndicator(props: {
  schedule: TeamScheduleAdminItemMonthDayIndicator_RoutineScheduleMonthly$key;
}) {
  const schedule = useFragment(
    graphql`
      fragment TeamScheduleAdminItemMonthDayIndicator_RoutineScheduleMonthly on RoutineScheduleMonthly {
        startDay
        endDay
      }
    `,
    props.schedule
  );
  return (
    <TeamScheduleListItemMonthlyRangeComponent
      startDay={schedule.startDay}
      endDay={schedule.endDay}
    />
  );
}

function WeekdayIndicator(props: {
  schedule: TeamScheduleAdminItemWeekdayIndicator_RoutineScheduleDaily$key;
}) {
  const schedule = useFragment(
    graphql`
      fragment TeamScheduleAdminItemWeekdayIndicator_RoutineScheduleDaily on RoutineScheduleDaily {
        weekdays {
          weekday
        }
      }
    `,
    props.schedule
  );

  const weekdaysString = useWeekdayFormattedString(
    schedule.weekdays.map((w) => w.weekday)
  );
  return weekdaysString ? (
    <AHText type="info-small" color="primary">
      {weekdaysString}
    </AHText>
  ) : null;
}
